/**
 * Das Blatt „Zähler getauscht" (Spec 46b, Fachregel 6/7): alte Nummer als
 * Lesezeile (macht die Reihenfolge sichtbar: Zähler mit der alten Nummer
 * anlegen → Tausch → neue Stände), Tauschdatum, Endstand des alten Zählers,
 * neue Nummer, Anfangsstand. Einstiege: `meter-replace` im ⋯-Menü und
 * `meter-capture-replace` unter der Meldung „Kleiner als der letzte Stand …".
 *
 * Geprüft wird am Server; Feldfehler (422) stehen am Feld, alles andere
 * (403/409/5xx/Netz) als Formularfehler mit „Erneut versuchen". Der Inhalt
 * hängt im `SheetContent` und beginnt dadurch bei jedem Öffnen frisch.
 */

import { useRef, useState } from 'react'
import type { RefObject, SubmitEvent } from 'react'

import { FIELD_CLASS, FormField } from '@/components/FormField'
import { Sheet, SheetContent, SheetFooter, SheetHeader, SheetTitle } from '@/components/ui/sheet'
import { useCreateReplacement } from '@/features/energy/api'
import type { MeterDetail } from '@/features/energy/api'
import { DigitFields } from '@/features/energy/DigitFields'
import { toErrorState } from '@/features/energy/form-error'
import type { ErrorState } from '@/features/energy/form-error'
import { canSaveReplace, emptyReplaceForm, toReplacementCreate } from '@/features/energy/replace-form'
import type { ReplaceFormValues } from '@/features/energy/replace-form'
import { todayInBerlin } from '@/lib/date'

const REPLACE_FIELDS = new Set(['replaced_on', 'end_value', 'new_serial', 'start_value'])

interface MeterReplaceSheetProps {
  readonly open: boolean
  readonly onOpenChange: (open: boolean) => void
  readonly meter: MeterDetail
  /** Nach erfolgreichem Tausch (das Blatt schließt selbst). */
  readonly onReplaced?: () => void
}

export function MeterReplaceSheet({ open, onOpenChange, meter, onReplaced }: MeterReplaceSheetProps) {
  const serialRef = useRef<HTMLInputElement>(null)

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent
        side="right"
        showCloseButton={false}
        data-testid="meter-replace-sheet"
        onOpenAutoFocus={(event) => {
          event.preventDefault()
          serialRef.current?.focus()
        }}
        className="data-[side=right]:!w-full data-[side=right]:sm:!max-w-none md:data-[side=right]:!w-[560px] md:data-[side=right]:!max-w-[560px]"
      >
        <ReplaceForm meter={meter} serialRef={serialRef} onClose={() => { onOpenChange(false) }} onReplaced={onReplaced} />
      </SheetContent>
    </Sheet>
  )
}

interface ReplaceFormProps {
  readonly meter: MeterDetail
  readonly serialRef: RefObject<HTMLInputElement | null>
  readonly onClose: () => void
  readonly onReplaced: (() => void) | undefined
}

function ReplaceForm({ meter, serialRef, onClose, onReplaced }: ReplaceFormProps) {
  const replaceMutation = useCreateReplacement()
  const today = todayInBerlin()
  const [form, setForm] = useState<ReplaceFormValues>(() => emptyReplaceForm(today))
  const [errors, setErrors] = useState<ErrorState>({})

  const canSave = canSaveReplace(form) && !replaceMutation.isPending
  const fieldError = (name: string) => (errors.field?.field === name ? errors.field.message : undefined)

  async function submit() {
    if (!canSave) return
    setErrors({})
    try {
      await replaceMutation.mutateAsync({ meterId: meter.id, body: toReplacementCreate(form) })
      onClose()
      onReplaced?.()
    } catch (error) {
      setErrors(toErrorState(error, REPLACE_FIELDS))
    }
  }

  function handleSubmit(event: SubmitEvent<HTMLFormElement>) {
    event.preventDefault()
    void submit()
  }

  return (
    <form onSubmit={handleSubmit} className="flex h-full flex-col">
      <SheetHeader>
        <SheetTitle className="text-section font-(--font-weight-section) text-text">Zähler getauscht</SheetTitle>
        <p className="text-label text-muted">
          {meter.label}
          {meter.house !== null && ` · ${meter.house.label}`}
        </p>
      </SheetHeader>

      <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4">
        <div className="flex flex-col gap-1">
          <span className="text-label text-muted">Alte Zählernummer</span>
          <p data-testid="meter-replace-old-serial" className="text-body text-text min-h-(--tap-min) py-2">
            {meter.serial}
          </p>
        </div>

        <FormField
          label="Tauschdatum"
          testId="meter-replace-replaced-on"
          apiField="replaced_on"
          errorPrefix="meter-replace"
          type="date"
          max={today}
          required
          value={form.replacedOn}
          onChange={(replacedOn) => {
            setForm((current) => ({ ...current, replacedOn }))
          }}
          error={errors.field}
        />

        <DigitField
          label="Endstand alter Zähler"
          idPrefix="meter-replace-end-value"
          apiField="end_value"
          meter={meter}
          typed={form.endTyped}
          lastValue={meter.last_reading?.value ?? null}
          error={fieldError('end_value')}
          onTypedChange={(endTyped) => {
            setForm((current) => ({ ...current, endTyped }))
          }}
        />

        <FormField
          ref={serialRef}
          label="Neue Zählernummer"
          testId="meter-replace-serial"
          apiField="new_serial"
          errorPrefix="meter-replace"
          autoCapitalize="characters"
          required
          value={form.newSerial}
          onChange={(newSerial) => {
            setForm((current) => ({ ...current, newSerial }))
          }}
          error={errors.field}
        />

        <DigitField
          label="Anfangsstand"
          idPrefix="meter-replace-start-value"
          apiField="start_value"
          meter={meter}
          typed={form.startTyped}
          lastValue={null}
          error={fieldError('start_value')}
          onTypedChange={(startTyped) => {
            setForm((current) => ({ ...current, startTyped }))
          }}
        />
      </div>

      <SheetFooter>
        {errors.general !== undefined && (
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p data-testid="meter-replace-error" role="alert" className="text-label text-status-overdue">
              Nicht gespeichert. {errors.general}
            </p>
            <button
              type="button"
              data-testid="meter-replace-retry"
              onClick={() => void submit()}
              className="text-accent text-label min-h-(--tap-min) hover:underline"
            >
              Erneut versuchen
            </button>
          </div>
        )}
        <div className="flex flex-wrap justify-end gap-2">
          <button type="button" data-testid="meter-replace-cancel" onClick={onClose} className={`text-text px-4 ${FIELD_CLASS}`}>
            Abbrechen
          </button>
          <button
            type="submit"
            data-testid="meter-replace-save"
            disabled={!canSave}
            aria-busy={replaceMutation.isPending}
            className="bg-accent rounded-card text-body px-4 text-accent-on transition-opacity duration-(--duration-fast) ease-(--ease-out) disabled:opacity-60"
          >
            Tausch speichern
          </button>
        </div>
      </SheetFooter>
    </form>
  )
}

interface DigitFieldProps {
  readonly label: string
  readonly idPrefix: string
  readonly apiField: string
  readonly meter: MeterDetail
  readonly typed: string
  readonly lastValue: number | null
  readonly error: string | undefined
  readonly onTypedChange: (typed: string) => void
}

/** Beschriftung, Ziffernfelder und Feldfehler eines Stands im Tausch-Blatt. */
function DigitField({ label, idPrefix, apiField, meter, typed, lastValue, error, onTypedChange }: DigitFieldProps) {
  const errorId = `meter-replace-error-${apiField}`
  return (
    <div className="flex flex-col gap-1">
      <span className="text-label text-muted">{label}</span>
      <DigitFields
        digits={meter.digits}
        typed={typed}
        onTypedChange={onTypedChange}
        lastValue={lastValue}
        unit={meter.unit}
        idPrefix={idPrefix}
        label={label}
      />
      {error !== undefined && (
        <p id={errorId} data-testid={errorId} role="alert" className="text-label text-status-overdue">
          {error}
        </p>
      )}
    </div>
  )
}
